﻿/* global jQuery */
import { WindowResizeMonitor } from '../main/WindowResizeMonitor.js';

export class JQueryPopupDialog {
  constructor(dispatcher, config) {
    this.config = config;
    this.dispatcher = dispatcher;
    this.closeButtonId = 'close-popup-btn';
    this.popupSelector = config.popupSelector; // Used by the process step classes
    this.buttons = [
      {
        id: this.closeButtonId,
        key: this.closeButtonId, // This is for our own lookup needs, see code below
        text: config.closeButtonText,
        click: () => this.close()
      }
    ];

    this.defaultPosition = { my: 'left top', at: 'left bottom', of: config.anchorSelector };

    const $popupElem = jQuery(config.popupSelector); // Assign it so we can inspect the contents
    // Note, we're assigning the $popup because it's referenced inside the popup callbacks below
    this.$popup = $popupElem.dialog({
      modal: true,
      autoOpen: false,
      width: 400,
      draggable: false,
      show: {
        effect: 'fade',
        duration: 300
      },
      hide: {
        effect: 'fade',
        duration: 300
      },
      position: this.defaultPosition,
      buttons: this.buttons,
      open: (event) => this.opened(event),
      close: (event) => this.closed(event)
    });
  }

  setWidth(width) {
    this.$popup.dialog('option', 'width', width);
  }

  applyButtonStyles() {
    const $buttons = this.$btnPanel.find('.ui-button');
    $buttons.removeClass('ui-button ui-corner-all ui-widget');
    $buttons.addClass('btn btn-sm');
    $buttons.each((index, buttonElem) => {
      const button = this.buttons[index];
      /* There's some weird behavior of the jQuery UI Dialog's button array options. If we add a
       * callback function directly as a property on the object, then jQuery will automatically
       * call that callback whenever the buttons are added. To avoid this we place the callback
       * inside a wrapper object. onAddButtonStyle is the wrapper object, and callback is the
       * child property of the wrapper. */
      const handled = button.onAddButtonStyle && button.onAddButtonStyle.callback &&
        button.onAddButtonStyle.callback(index, buttonElem, button);
      if (!handled) {
        if (buttonElem.id === this.closeButtonId) {
          buttonElem.classList.add('btn-outline-secondary');
        } else {
          buttonElem.classList.add('btn-outline-success');
        }
      }
    });
  }

  findButtonById(id) {
    return this.$btnPanel.find(`#${id}`);
  }

  findButtonByKey(key) {
    return this.$btnPanel.find(`[key="${key}"]`);
  }

  /** Handler for the popup open event. */
  opened(event) {
    this.$widget = this.$popup.dialog('widget');
    // Note, the ui-widget-overlay class is added dynamically by the jquery-ui framework
    const $overlay = jQuery('.ui-widget-overlay').last();
    $overlay.unbind();
    $overlay.click(() => this.close());
    // Restyle the buttons for Bootstrap UI instead of jQuery UI:
    this.$btnPanel = this.$widget.find('.ui-dialog-buttonpane');
    this.$btnPanel.addClass('button-block'); // To apply same .button-block .btn/.btn-sm classes as the rest of our block pages
    this.applyButtonStyles();
  }

  /** Handler for the popup close event. */
  closed(event) {
    this.dispatcher.dispatch({ id: this.config.closeEventId });
    // Restore the default position for next time it's opened:
    this.setPosition(this.defaultPosition);
  }

  open() {
    this.$popup.dialog('open'); // Opens the popup and triggers the popup's open event/callback
  }

  close() {
    this.$popup.dialog('close'); // Closes the popup and triggers the popup's close event/callback
  }

  hideBody() {
    if (this.content && this.content.length > 0) {
      console.log(`JQueryPopupDialog: hiding body for ${this.content.attr('class')}`);
      this.content.hide();
    }
  }

  showBody(contentSelector) {
    if (contentSelector) {
      //this.content = jQuery(contentSelector);
      this.content = this.findChildElement(contentSelector);
      if (this.content.length > 0) {
        console.log(`JQueryPopupDialog: showing body for ${contentSelector}`);
        this.content.show();
      } else {
        console.log(`JQueryPopupDialog: body element for ${contentSelector} was not found!`);
      }
    }
  }

  /**
   * Adds the buttons to the popup dialog's button pane
   * @param {any} buttons An array of button definition objects. Each button must have a key property.
   * @returns The updated buttons array (old plus new buttons)
   */
  addButtons(buttons) {
    // This adds the new buttons to the end:
    //buttons.forEach(button => this.buttons.push(button));
    /* This inserts the buttons at the front. The funny looking statement reverses the order of the
     * buttons array w/o making changes to the array itself: */
    [...buttons].reverse().forEach(button => {
      if (!button.key) {
        throw new Error('In order to remove buttons later, each button object must have a unique key property!');
      }
      this.buttons.unshift(button); // Inserts the button at the front of the array
    });
    // Updates the popup dialog and DOM immediately:
    this.$popup.dialog('option', 'buttons', this.buttons);
    // All buttons have now been recreated and reinserted into the DOM. Ensure we re-apply our styles:
    this.applyButtonStyles();
    return this.buttons;
  }

  /**
   * Removes the buttons from the popup dialog's button pane
   * @param {array} buttons Buttons to be removed. Each button must have a key property.
   * @param {bool} keepCurrentPosition Removing the buttons have the side effect of resetting
   * the dialog's original position. Set keepCurrentPosition to true if you don't want that to
   * happen.
   * @returns The updated buttons array (old plus new buttons)
   */
  removeButtons(buttons, keepCurrentPosition) {
    if (buttons) {
      // Iterate and remove buttons:
      for (let i = this.buttons.length - 1; i >= 0; i--) {
        for (let j = 0; j < buttons.length; j++) {
          if (buttons[j].key === this.buttons[i].key) {
            this.buttons.splice(i, 1);
            break;
          }
        }
      }

      const currentPos = keepCurrentPosition ? this.$widget.position() : null;

      // Updates the popup dialog and DOM immediately:
      this.$popup.dialog('option', 'buttons', this.buttons);
      // All buttons have now been recreated and reinserted into the DOM. Ensure we re-apply our styles:
      this.applyButtonStyles();

      if (keepCurrentPosition) {
        this.$popup.dialog('option', 'position', {
          my: "left top",
          at: "left+" + currentPos.left + " top+" + currentPos.top,
          of: window
        });
      }
    }
    return this.buttons;
  }

  startMonitorWindowResizing() {
    if (this.onWindowResized) {
      if (!this.resizeMonitor) {
        this.resizeMonitor = new WindowResizeMonitor(/*startImmediately:*/ true);
        this.resizeMonitor.onResize = mon => {
          if (this.onWindowResized) {
            this.onWindowResized(mon);
          }
        };
      } else {
        this.resizeMonitor.toggleMonitor(true);
      }
    }
  }

  stopMonitorWindowResizing() {
    if (this.resizeMonitor) {
      this.resizeMonitor.toggleMonitor(false);
    }
  }

  setPosition(position) {
    this.$popup.dialog('option', 'position', position);
  }

  animateToPosition(left, top, skipAnimation, makeItDraggable) {
    if (skipAnimation) {
      this.$popup.parent().css({ left: left, top: top });
    } else {
      // Animate the dialog's position
      this.$popup.parent().animate({ left: left, top: top }, 1000); // Duration of the animation in milliseconds
    }
    if (makeItDraggable) {
      this.$popup.dialog("option", "draggable", true);
    }
  }

  animateDialogToCenter(skipAnimation) {
    const $parent = this.$popup.parent(); // Need the parent to include header and button bar in the dimensions
    const $window = jQuery(window);
    // Calculate the center position
    const left = ($window.width() - $parent.outerWidth()) / 2;
    const top = (($window.height() - $parent.outerHeight()) / 2) + $window.scrollTop();
    this.animateToPosition(left, top, skipAnimation, /*makeItDraggable:*/ true);
  }

  animateDialogToTopRight(skipAnimation) {
    const $parent = this.$popup.parent(); // Need the parent to include header and button bar in the dimensions
    const $window = jQuery(window);
    // Calculate the top right position
    const rightMargin = 20; // Space from the right edge of the window
    const topMargin = 20; // Space from the top of the window
    const left = $window.width() - $parent.outerWidth() - rightMargin;
    const top = topMargin + $window.scrollTop();
    this.animateToPosition(left, top, skipAnimation, /*makeItDraggable:*/ true);
  }

  animateDialogToVertCenterAndHorizRight(skipAnimation, rightMargin = 20) {
    const $dialog = this.$popup;
    const $window = jQuery(window);
    const top = (($window.height() - $dialog.outerHeight()) / 2) + $window.scrollTop();
    // Calculate the right position
    const left = $window.width() - $dialog.outerWidth() - rightMargin;
    // Animate the dialog's position
    this.animateToPosition(left, top, skipAnimation, /*makeItDraggable:*/ true);
  }

  animateDialogToVertTopAndHorizCenter(skipAnimation, topMargin = 20) {
    const $parent = this.$popup.parent(); // Need the parent to include header and button bar in the dimensions
    const $window = jQuery(window);
    const left = ($window.width() - $parent.outerWidth()) / 2;
    const top = topMargin + $window.scrollTop();
    // Animate the dialog's position
    this.animateToPosition(left, top, skipAnimation, /*makeItDraggable:*/ true);
  }

  animateDialogToVertBottomAndHorizCenter(skipAnimation, bottomMargin = 20) {
    const $parent = this.$popup.parent(); // Need the parent to include header and button bar in the dimensions
    const $window = jQuery(window);
    const left = ($window.width() - $parent.outerWidth()) / 2;
    const top = $window.height() - $parent.outerHeight() - bottomMargin + $window.scrollTop();
    // Animate the dialog's position
    this.animateToPosition(left, top, skipAnimation, /*makeItDraggable:*/ true);
  }

  findChildElement(selector) {
    return this.$widget.find(selector);
  }

  displayMessage(message, messageClass) {
    const $dialog = this.$popup;
    // Remove previous classes and set the new one
    var $message = $dialog.find('.popup-message').attr('class', 'popup-message ' + messageClass);
    if (message) {
      $message.text(message).show();
    } else {
      $message.hide(); // No message provided, hide the message area
    }
  }

  displayInfo(message) {
    this.displayMessage(message, 'popup-info');
  }

  displayWarning(message) {
    this.displayMessage(message, 'popup-warning');
  }

  displayError(message) {
    this.displayMessage(message, 'popup-error');
  }
}